import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import { useFactoryDetailsMaster } from '../../../hooks/useFactoryDetailsMaster';
import '../../../assets/styles/shipping_manifest_list.scss';
import ShippingManifestDetailsModal from '../../../components/ShippingManifestDetailsModal';
import { transformManifestData } from '../../../utils/shippingManifestView';

export default function ShippingManifestList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [acceptManifest, setAcceptManifest] = useState(null);
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [manifests, setManifests] = useState([]);
  const [acceptingShipment, setAcceptingShipment] = useState(false);
  
  // Master Data Hooks
  const { shippingTermsOptions, modeOfTransportOptions } = useFactoryDetailsMaster();
  const [acceptForm, setAcceptForm] = useState({
    dateReceived: '',
    receivedBy: '',
    packagesReceived: '',
    condition: 'Good Condition',
    notes: '',
    uploadName: '',
    uploadFile: null,
    inspectionChecklist: {}
  });

  // Fetch shipping manifests for buyer
  useEffect(() => {
    const fetchManifests = async () => {
      try {
        setLoading(true);
        const response = await CommonService.get('/shipping-manifest/buyer/my-manifests');
        if (response && response.success && response.data) {
          const manifestsData = Array.isArray(response.data) ? response.data : [response.data];
          // Transform backend data to frontend format
          const transformedManifests = manifestsData
            .map((manifest) => transformManifestData(manifest))
            .filter(Boolean);
          setManifests(transformedManifests);
        } else {
          setManifests([]);
        }
      } catch (error) {
        console.error('Error fetching shipping manifests:', error);
        toast.error('Failed to fetch shipping manifests');
        setManifests([]);
      } finally {
        setLoading(false);
      }
    };

    fetchManifests();
  }, []);

  // Transform is now imported from shared util to keep "View Details" consistent across pages.

  const statusOptions = [
    'All Statuses',
    'Created',
    'Accepted'
  ];

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to get mode of transport label
  const getModeOfTransportLabel = (modeValue) => {
    if (!modeValue) return 'N/A';
    if (!modeOfTransportOptions || modeOfTransportOptions.length === 0) return modeValue;
    
    const modeValueNum = isNaN(modeValue) ? null : Number(modeValue);
    const foundByValue = modeOfTransportOptions.find(opt => {
      if (String(opt.value) === String(modeValue)) return true;
      if (modeValueNum !== null && !isNaN(opt.value)) {
        return Number(opt.value) === modeValueNum;
      }
      return false;
    });
    
    if (foundByValue) {
      return foundByValue.label || modeValue;
    }
    
    const foundByLabel = modeOfTransportOptions.find(opt => 
      opt.label === String(modeValue) || 
      opt.label?.toLowerCase() === String(modeValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return modeValue;
  };

  // Helper function to get incoterms label
  const getIncotermsLabel = (incotermsValue) => {
    if (!incotermsValue) return 'N/A';
    if (!shippingTermsOptions || shippingTermsOptions.length === 0) return incotermsValue;
    
    const incotermsValueNum = isNaN(incotermsValue) ? null : Number(incotermsValue);
    const foundByValue = shippingTermsOptions.find(opt => {
      if (String(opt.value) === String(incotermsValue)) return true;
      if (incotermsValueNum !== null && !isNaN(opt.value)) {
        return Number(opt.value) === incotermsValueNum;
      }
      return false;
    });
    
    if (foundByValue) {
      return foundByValue.label || incotermsValue;
    }
    
    const foundByLabel = shippingTermsOptions.find(opt => 
      opt.label === String(incotermsValue) || 
      opt.label?.toLowerCase() === String(incotermsValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return incotermsValue;
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'Created': 'sml-status-in-transit',
      'Accepted': 'sml-status-accepted',
      'Draft': 'sml-status-pending',
      'Pending': 'sml-status-pending'
    };
    return statusMap[status] || 'sml-status-pending';
  };

  const renderStatusIcon = (status) => {
    if (status === 'Created') {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"></path>
          <path d="M15 18H9"></path>
          <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"></path>
          <circle cx="17" cy="18" r="2"></circle>
          <circle cx="7" cy="18" r="2"></circle>
        </svg>
      );
    }
    if (status === 'Accepted') {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
          <path d="m9 11 3 3L22 4"></path>
        </svg>
      );
    }
    return null;
  };

  const renderStatusBadge = (status) => (
    <span className={`sml-status-tag ${getStatusClass(status)}`}>
      {renderStatusIcon(status)}
      {status}
    </span>
  );

  const filteredManifests = (Array.isArray(manifests) ? manifests : [])
    .filter((m) => m && typeof m === 'object')
    .filter((manifest) => {
    // Exclude draft manifests
    if (manifest.status === 'Draft' || manifest.status === 'draft') {
      return false;
    }

    const normalizedQuery = searchQuery.trim().toLowerCase();
    const searchFields = [
      manifest.manifestNumber,
      manifest.exporter,
      manifest.importer,
      manifest.billOfLading,
      manifest.airWaybill,
      manifest.poNumber,
      manifest.invoiceNumber,
      manifest.lrNumber,
      manifest.deliveryPortCode
    ].filter(Boolean);

    const matchesSearch =
      normalizedQuery === ''
        ? true
        : searchFields.some((field) => String(field).toLowerCase().includes(normalizedQuery));
    
    const matchesStatus = statusFilter === 'All Statuses' || manifest.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleViewDetails = async (manifestId) => {
    try {
      // Fetch full manifest details with items
      const response = await CommonService.get(`/shipping-manifest/${manifestId}`);
      if (response && response.success && response.data) {
        // Transform the fetched manifest data to match the expected format
        const transformedManifest = transformManifestData(response.data);
        if (!transformedManifest) {
          toast.error('Failed to load manifest details');
          return;
        }
        setSelectedManifest(transformedManifest);
        setIsModalOpen(true);
      } else {
        // Fallback to existing manifest data from list
        const manifest = manifests.find(item => item.id === manifestId);
        if (manifest) {
          setSelectedManifest(manifest);
          setIsModalOpen(true);
        }
      }
    } catch (error) {
      console.error('Error fetching manifest details:', error);
      toast.error('Failed to fetch manifest details');
      // Fallback to existing manifest data from list
      const manifest = manifests.find(item => item.id === manifestId);
      if (manifest) {
        setSelectedManifest(manifest);
        setIsModalOpen(true);
      }
    }
  };

  const handleDownload = (manifestId) => {
    // Download manifest PDF
    console.log('Download manifest:', manifestId);
  };

  const handleAcceptShipment = (manifestId) => {
    const manifest = manifests.find(item => item.id === manifestId);
    if (manifest) {
      // Don't allow accepting if already accepted
      if (manifest.status === 'Accepted') {
        toast.info('This shipment has already been accepted');
        return;
      }
      
      setAcceptManifest(manifest);
      // Set today's date as default for date received
      const today = new Date().toISOString().split('T')[0];
      setAcceptForm({
        dateReceived: formatDate(manifest.actualArrival) || today,
        receivedBy: '',
        packagesReceived: manifest.packages?.toString() || '0',
        condition: 'Good Condition',
        notes: '',
        uploadName: '',
        uploadFile: null,
        inspectionChecklist: {}
      });
      setIsAcceptModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedManifest(null);
  };

  const handleCloseAcceptModal = () => {
    setIsAcceptModalOpen(false);
    setAcceptManifest(null);
  };

  const handleAcceptFormChange = (field, value) => {
    setAcceptForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAcceptFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAcceptForm(prev => ({
        ...prev,
        uploadFile: file,
        uploadName: file.name
      }));
    }
  };

  // Upload POD file
  const uploadPODFile = async (file, manifestId) => {
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('category', 'company');
      uploadFormData.append('companyName', acceptManifest?.exporter || 'Default_Company');
      uploadFormData.append('fileType', 'pod');

      const uploadResponse = await CommonService.post('/upload/single', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (uploadResponse && uploadResponse.success && uploadResponse.data) {
        return uploadResponse.data.filePath;
      }
      throw new Error('File upload failed');
    } catch (error) {
      console.error('Error uploading POD file:', error);
      throw error;
    }
  };

  const handleAcceptSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptManifest?.id) {
      toast.error('Manifest ID is missing');
      return;
    }

    // Validate required fields
    if (!acceptForm.dateReceived || !acceptForm.receivedBy || !acceptForm.packagesReceived) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setAcceptingShipment(true);

      // Upload POD file if provided
      let podFilePath = null;
      if (acceptForm.uploadFile) {
        try {
          podFilePath = await uploadPODFile(acceptForm.uploadFile, acceptManifest.id);
        } catch (uploadError) {
          console.error('Error uploading POD file:', uploadError);
          toast.warning('POD file upload failed, but continuing with acceptance...');
        }
      }

      // Prepare acceptance data
      // Save inspection checklist in separate column
      const acceptanceData = {
        actual_arrival_date: acceptForm.dateReceived,
        accepted_by: acceptForm.receivedBy,
        acceptance_notes: acceptForm.notes || null,
        inspection_checklist: acceptForm.inspectionChecklist || {},
        pod_file_path: podFilePath
      };

      // Call the API to accept the shipment
      const response = await CommonService.put(
        `/shipping-manifest/${acceptManifest.id}/accept`,
        acceptanceData
      );

      if (response && response.success) {
        toast.success('Shipment accepted successfully');
        
        // Refresh the manifest list to get updated data from backend
        try {
          const refreshResponse = await CommonService.get('/shipping-manifest/buyer/my-manifests');
          if (refreshResponse && refreshResponse.success && refreshResponse.data) {
            const manifestsData = Array.isArray(refreshResponse.data) ? refreshResponse.data : [refreshResponse.data];
            const transformedManifests = manifestsData
              .map((manifest) => transformManifestData(manifest))
              .filter(Boolean);
            setManifests(transformedManifests);
          }
        } catch (refreshError) {
          console.error('Error refreshing manifest list:', refreshError);
          // Fallback: update local state
          setManifests(prevManifests =>
            (Array.isArray(prevManifests) ? prevManifests : [])
              .filter(Boolean)
              .map(manifest =>
                manifest.id === acceptManifest.id
                ? {
                    ...manifest,
                    status: 'Accepted',
                    actualArrival: acceptForm.dateReceived,
                    acceptanceDetails: {
                      dateReceived: acceptForm.dateReceived,
                      receivedBy: acceptForm.receivedBy,
                      packagesReceived: acceptForm.packagesReceived,
                      condition: acceptForm.condition,
                      additionalNotes: acceptForm.notes,
                      podFile: acceptForm.uploadFile ? {
                        name: acceptForm.uploadName,
                        uploadedAt: new Date().toISOString()
                      } : null
                    }
                  }
                  : manifest
              )
          );
        }

        // Close the modal
        handleCloseAcceptModal();
      } else {
        toast.error(response?.error || 'Failed to accept shipment');
      }
    } catch (error) {
      console.error('Error accepting shipment:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Failed to accept shipment');
    } finally {
      setAcceptingShipment(false);
    }
  };

  const modalTitleId = 'sml-view-modal-title';
  const modalDescriptionId = 'sml-view-modal-description';

  const renderDocumentRow = (manifest, docKey, label, isRequired = false) => {
    const uploaded = manifest.documents?.[docKey];
    const fileInfo = manifest.documentFiles?.[docKey];
    const hasFile = uploaded && fileInfo?.name;

    return (
      <div className="sml-doc-row" key={`${docKey}-${manifest.id}`}>
        <div className="sml-doc-info">
          <p className="sml-doc-label">
            {label}
            {isRequired && <span className="sml-doc-required">*</span>}
          </p>
          {hasFile ? (
            <>
              <p className="sml-doc-file">{fileInfo.name}</p>
              {fileInfo?.type && <p className="sml-doc-meta">{fileInfo.type}</p>}
            </>
          ) : (
            <p className="sml-doc-file sml-doc-file-muted">No file uploaded</p>
          )}
        </div>
        {hasFile ? (
          <button
            className="sml-doc-view-btn"
            type="button"
            onClick={() => handleDownloadDocument(docKey, fileInfo)}
          >
            View
          </button>
        ) : (
          <span className="sml-chip sml-chip-muted">Pending</span>
        )}
      </div>
    );
  };

  const handleDownloadDocument = (docKey, fileInfo) => {
    console.log('Download document:', docKey, fileInfo);
  };

  return (
    <div className="shipping-manifest-list">
      <div className="sml-main-content">
        {/* Header Section */}
        <div className="sml-header">
          <div className="sml-header-left">
            <nav aria-label="breadcrumb" className="sml-breadcrumb">
              <ol className="sml-breadcrumb-list">
                <li className="sml-breadcrumb-item">
                  <a href="/" className="sml-breadcrumb-link">Home</a>
                </li>
                <li className="sml-breadcrumb-item sml-breadcrumb-active" aria-current="page">
                  Shipping Manifest Acceptance
                </li>
              </ol>
            </nav>
            <h1 className="sml-main-title">Shipping Manifest Acceptance</h1>
            <p className="sml-subtitle">Track incoming shipments and acknowledge receipt</p>
          </div>
          <div className="sml-header-right">
            <button className="sml-back-btn" onClick={() => navigate(-1)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m12 19-7-7 7-7"></path>
                <path d="M19 12H5"></path>
              </svg>
              Back
            </button>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="sml-search-filter-section">
          <div className="sml-search-filter-row">
            <div className="sml-search-wrapper">
              <svg 
                className="sml-search-icon" 
                xmlns="http://www.w3.org/2000/svg" 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="sml-search-input"
                placeholder="Search by manifest, PO, invoice, exporter, or AWB/BL..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="sml-filter-wrapper">
              <select
                className="sml-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Manifest Cards */}
        <div className="sml-manifests-container">
          {loading ? (
            <div className="sml-empty-state">
              <p>Loading shipping manifests...</p>
            </div>
          ) : filteredManifests.length === 0 ? (
            <div className="sml-empty-state">
              <p>No manifests found matching your search criteria.</p>
            </div>
          ) : (
            filteredManifests.map(manifest => (
              <div key={manifest.id} className="sml-manifest-card">
                <div className="sml-card-content-wrapper">
                  <div className="sml-card-main-row">
                    <div className="sml-card-left-section">
                      <div className="sml-manifest-header-row">
                        {manifest.type === 'SEA' ? (
                          <svg 
                            className="sml-transport-icon sml-transport-icon-ship"
                            xmlns="http://www.w3.org/2000/svg" 
                            width="24" 
                            height="24" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M12 10.189V14"></path>
                            <path d="M12 2v3"></path>
                            <path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"></path>
                            <path d="M19.38 20A11.6 11.6 0 0 0 21 14l-8.188-3.639a2 2 0 0 0-1.624 0L3 14a11.6 11.6 0 0 0 2.81 7.76"></path>
                            <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1s1.2 1 2.5 1c2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"></path>
                          </svg>
                        ) : (
                          <svg 
                            className="sml-transport-icon sml-transport-icon-plane"
                            xmlns="http://www.w3.org/2000/svg" 
                            width="24" 
                            height="24" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"></path>
                          </svg>
                        )}
                        <div className="sml-manifest-title-group">
                          <h3 className="sml-manifest-number">{manifest.manifestNumber}</h3>
                          {manifest.billOfLading && (
                            <p className="sml-reference">Bill of Lading (B/L): {manifest.billOfLading}</p>
                          )}
                          {manifest.airWaybill && (
                            <p className="sml-reference">Air Waybill (AWB): {manifest.airWaybill}</p>
                          )}
                        </div>
                    <div className="sml-card-actions">
                      <button 
                        className={`sml-action-btn sml-btn-status ${manifest.status === 'Accepted' ? 'sml-btn-status-accepted' : 'sml-btn-status-pending'}`}
                        disabled
                        title={manifest.status || 'Pending'}
                      >
                        {manifest.status === 'Accepted' ? (
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          >
                            <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                            <path d="m9 11 3 3L22 4"></path>
                          </svg>
                        ) : (
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                          </svg>
                        )}
                        {manifest.status === 'Accepted' ? 'Accepted' : 'Pending'}
                      </button>
                      <button 
                        className="sml-action-btn sml-btn-view"
                        onClick={() => handleViewDetails(manifest.id)}
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                        View Details
                      </button>
                      <button 
                        className="sml-action-btn sml-btn-download"
                        onClick={() => handleDownload(manifest.id)}
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <path d="M12 15V3"></path>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <path d="m7 10 5 5 5-5"></path>
                        </svg>
                        Download
                      </button>
                      {manifest.status === 'Created' && (
                        <button 
                          className="sml-action-btn sml-btn-accept"
                          onClick={() => handleAcceptShipment(manifest.id)}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          Accept Shipment
                        </button>
                      )}
                    </div>
                  </div>

                      <div className="sml-details-grid">
                        <div className="sml-grid-item">
                          <p className="sml-detail-label">Exporter / Shipper</p>
                          <p className="sml-detail-value">{manifest?.exporter || '—'}</p>
                          {manifest?.exporterLocation && (
                            <p className="sml-detail-subtext">{manifest.exporterLocation}</p>
                          )}
                        </div>
                        <div className="sml-grid-item">
                          <p className="sml-detail-label">Consignee / Importer</p>
                          <p className="sml-detail-value">{manifest?.importer || '—'}</p>
                          {manifest?.importerLocation && (
                            <p className="sml-detail-subtext">{manifest.importerLocation}</p>
                          )}
                        </div>
                        <div className="sml-grid-item">
                          <p className="sml-detail-label">Packages / Weight</p>
                          <p className="sml-detail-value">
                            {manifest?.packages ?? 0} pkgs · {manifest?.weight || '0 kg'}
                          </p>
                          {manifest?.lastUpdated && (
                            <p className="sml-detail-subtext">
                              Last updated {formatDate(manifest.lastUpdated)}
                            </p>
                          )}
                        </div>
                        <div className="sml-grid-item">
                          <p className="sml-detail-label">PO / Invoice</p>
                          <p className="sml-detail-value">{manifest.poNumber}</p>
                          <p className="sml-detail-subtext">
                            Invoice: {manifest.invoiceNumber}
                            {manifest.lrNumber && (
                              <>
                                <br />
                                LR: {manifest.lrNumber}
                              </>
                            )}
                          </p>
                        </div>
                        <div className="sml-grid-item">
                          <p className="sml-detail-label">Mode / Incoterms</p>
                          <p className="sml-detail-value">{getModeOfTransportLabel(manifest.mode)}</p>
                          {manifest.incoterms && (
                            <p className="sml-detail-subtext">{getIncotermsLabel(manifest.incoterms)}</p>
                          )}
                          {manifest.expectedDeparture && (
                            <p className="sml-detail-subtext">
                              ETD: {formatDate(manifest.expectedDeparture)}
                            </p>
                          )}
                        </div>
                        <div className="sml-grid-item">
                          <p className="sml-detail-label">Port & Route</p>
                          <p className="sml-detail-value">{manifest.deliveryPort}</p>
                          {manifest.route && (
                            <p className="sml-detail-subtext">
                              {manifest.route}
                              {manifest.deliveryPortCode ? ` (${manifest.deliveryPortCode})` : ''}
                            </p>
                          )}
                          {manifest.expectedArrival && (
                            <p className="sml-detail-subtext">ETA: {formatDate(manifest.expectedArrival)}</p>
                          )}
                          {manifest.actualArrival && (
                            <p className="sml-actual-arrival">Actual: {formatDate(manifest.actualArrival)}</p>
                          )}
                        </div>
                      </div>

                      <div className="sml-status-tags">
                        {renderStatusBadge(manifest.status)}
                        <span className="sml-info-tag">
                          {manifest.invoiceCount} Invoice{manifest.invoiceCount !== 1 ? 's' : ''}
                        </span>
                        {manifest.containerCount > 0 && (
                          <span className="sml-info-tag">
                            {manifest.containerCount} Container{manifest.containerCount !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <ShippingManifestDetailsModal
        open={Boolean(isModalOpen && selectedManifest)}
        manifest={selectedManifest}
        onClose={handleCloseModal}
        shippingTermsOptions={shippingTermsOptions}
        modeOfTransportOptions={modeOfTransportOptions}
      />

      {false && (
        <>
            <div className="sml-view-section">
              <h4 className="sml-view-section-title">Vehicle & Driver Details</h4>
              <div className="sml-view-details-grid sml-view-details-grid-five">
                <div>
                  <p className="sml-view-detail-label">Vehicle Type</p>
                  <p className="sml-view-detail-value">{selectedManifest.vehicleDetails?.vehicleType || '—'}</p>
                </div>
                <div>
                  <p className="sml-view-detail-label">Vehicle Number</p>
                  <p className="sml-view-detail-value">{selectedManifest.vehicleDetails?.vehicleNumber || '—'}</p>
                </div>
                <div>
                  <p className="sml-view-detail-label">Vehicle Capacity</p>
                  <p className="sml-view-detail-value">{selectedManifest.vehicleDetails?.vehicleCapacity || '—'}</p>
                </div>
                <div>
                  <p className="sml-view-detail-label">GPS Tracking ID</p>
                  <p className="sml-view-detail-value">{selectedManifest.vehicleDetails?.gpsTrackingId || '—'}</p>
                </div>
                <div>
                  <p className="sml-view-detail-label">GPS Provider</p>
                  <p className="sml-view-detail-value">{selectedManifest.vehicleDetails?.gpsProvider || '—'}</p>
                </div>
                <div>
                  <p className="sml-view-detail-label">Insurance Policy No.</p>
                  <p className="sml-view-detail-value">{selectedManifest.vehicleDetails?.insurancePolicyNo || '—'}</p>
                </div>
                <div>
                  <p className="sml-view-detail-label">Insurance Provider</p>
                  <p className="sml-view-detail-value">{selectedManifest.vehicleDetails?.insuranceProvider || '—'}</p>
                </div>
                <div>
                  <p className="sml-view-detail-label">Driver Name</p>
                  <p className="sml-view-detail-value">{selectedManifest.vehicleDetails?.driverName || '—'}</p>
                </div>
                <div>
                  <p className="sml-view-detail-label">Driver License No.</p>
                  <p className="sml-view-detail-value">{selectedManifest.vehicleDetails?.driverLicenseNo || '—'}</p>
                </div>
                <div>
                  <p className="sml-view-detail-label">Driver Contact Number</p>
                  <p className="sml-view-detail-value">{selectedManifest.vehicleDetails?.driverContactNumber || '—'}</p>
                </div>
              </div>
            </div>

            <div className="sml-view-section">
              <h4 className="sml-view-section-title">Item &amp; Package Details</h4>
              <div className="sml-view-table-wrapper">
                <table className="sml-view-table sml-view-table-compact">
                  <thead>
                    <tr>
                      <th>HSN / Product Code</th>
                      <th>Product Description</th>
                      <th>Qty Shipped</th>
                      <th>Qty Total</th>
                      <th>Shipping Qty</th>
                      <th>UOM</th>
                      <th>Package Type</th>
                      <th>No. Packages</th>
                      <th>Weight / Pkg</th>
                      <th className="sml-text-right">Total Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedManifest.packageItems && selectedManifest.packageItems.length > 0 ? (
                      <>
                        {selectedManifest.packageItems.map((item, index) => (
                          <tr key={`pkg-${item.hsnProductCode}-${index}`}>
                            <td>{item.hsnProductCode}</td>
                            <td>{item.productDescription}</td>
                            <td>{item.qtyShipped}</td>
                            <td>{item.qtyTotal}</td>
                            <td>{item.shippingQty}</td>
                            <td>{item.uom}</td>
                            <td>{item.packageType}</td>
                            <td>{item.noPackages}</td>
                            <td>{item.weightPerPkg}</td>
                            <td className="sml-text-right">{item.totalWeight}</td>
                          </tr>
                        ))}
                        <tr className="sml-view-table-total-row">
                           <td colSpan="1"></td>
                          <td colSpan="1" className="sml-view-table-total-label">Totals</td>
                          <td colSpan="5"></td>
                          <td className="">{selectedManifest.totals?.packages || selectedManifest.packages}</td>
                          <td />
                          <td className="sml-text-right">{selectedManifest.totals?.grossWeight || selectedManifest.weight}</td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan="10" className="sml-view-table-empty">No package items recorded for this manifest.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>


            <div className="sml-view-section">
              <h4 className="sml-view-section-title">Supporting Documents</h4>
              <div className="sml-doc-section">
                <div className="sml-doc-column sml-doc-column--required">
                  <p className="sml-doc-column-title">Required Documents</p>
                  <div className="sml-doc-required-grid">
                    {renderDocumentRow(selectedManifest, 'invoice', 'Invoice Copy', true)}
                    {renderDocumentRow(selectedManifest, 'ewayBill', 'E-way Bill', true)}
                  </div>
                  <p className="sml-doc-column-title sml-doc-optional-title">Optional Documents</p>
                  <div className="sml-doc-required-grid sml-doc-optional-grid">
                    <div className="sml-doc-row-wrapper">
                      {renderDocumentRow(selectedManifest, 'packingList', 'Packing List')}
                    </div>
                    <div className="sml-doc-row-wrapper">
                      {renderDocumentRow(selectedManifest, 'qualityCert', 'Quality Certificate')}
                    </div>
                    <div className="sml-doc-row-wrapper">
                      {renderDocumentRow(selectedManifest, 'certOfOrigin', 'Certificate of Origin')}
                    </div>
                    <div className="sml-doc-row-wrapper">
                      {renderDocumentRow(selectedManifest, 'otherDocs', 'Other Documents')}
                    </div>
                  </div>
                </div>
              </div>
            </div>


            {selectedManifest.additionalNotes && (
              <div className="sml-view-section">
                <h4 className="sml-view-section-title">Additional Notes / Remarks</h4>
              <div className="sml-view-card sml-view-card-muted sml-view-notes-card">
                <p className="sml-view-card-value">{selectedManifest.additionalNotes}</p>
              </div>
              </div>
            )}

            {selectedManifest.acceptanceDetails && (
              <div className="sml-view-section">
                <h4 className="sml-view-section-title">Acceptance Details</h4>

                <div className="sml-view-card sml-view-acceptance-card">
                  <h5 className="sml-view-acceptance-heading">Receiving Details</h5>
                  <div className="sml-view-acceptance-grid">
                    <div>
                      <p className="sml-view-detail-label">Date Received</p>
                      <p className="sml-view-detail-value">
                        {selectedManifest.acceptanceDetails.dateReceived
                          ? formatDate(selectedManifest.acceptanceDetails.dateReceived)
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="sml-view-detail-label">Accepted Date</p>
                      <p className="sml-view-detail-value">
                        {selectedManifest.acceptanceDetails.acceptedDate
                          ? formatDate(selectedManifest.acceptanceDetails.acceptedDate)
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="sml-view-detail-label">Received By</p>
                      <p className="sml-view-detail-value">{selectedManifest.acceptanceDetails.receivedBy || '—'}</p>
                    </div>
                    <div>
                      <p className="sml-view-detail-label">Packages Received</p>
                      <p className="sml-view-detail-value">
                        {selectedManifest.acceptanceDetails.packagesReceived || selectedManifest.packages || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="sml-view-detail-label">Condition</p>
                      <p className="sml-view-detail-value">{selectedManifest.acceptanceDetails.condition || '—'}</p>
                    </div>
                  </div>
                </div>

                <div className="sml-view-acceptance-row">
                  <div className="sml-view-card sml-view-acceptance-panel">
                    <h5 className="sml-view-acceptance-heading">Inspection Checklist</h5>
                    {(() => {
                      const checklistItems = [
                        { key: 'packagingIntact', label: 'Packaging is intact and undamaged' },
                        { key: 'correctQuantity', label: 'Correct quantity received as per manifest' },
                        { key: 'noVisibleDamage', label: 'No visible damage to goods' },
                        { key: 'sealsIntact', label: 'Container seals are intact and match manifest' },
                        { key: 'documentsComplete', label: 'All shipping documents are complete and correct' }
                      ];
                      
                      // Filter to show only checked items
                      const inspectionChecklist = selectedManifest.acceptanceDetails?.inspectionChecklist || {};
                      const checkedItems = checklistItems.filter(item => {
                        const value = inspectionChecklist[item.key];
                        // Check for true, 'true', 1, or '1'
                        const isChecked = value === true || value === 'true' || value === 1 || value === '1';
                        return isChecked;
                      });

                      if (checkedItems.length === 0) {
                        return (
                          <p className="sml-view-acceptance-note" style={{ fontStyle: 'italic', color: '#666' }}>
                            No inspection items were checked.
                          </p>
                        );
                      }

                      return (
                        <ul className="sml-view-acceptance-checklist">
                          {checkedItems.map(item => (
                            <li
                              key={item.key}
                              className="sml-check-complete"
                            >
                              <span className="sml-view-acceptance-check-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 6 9 17l-5-5" />
                                </svg>
                              </span>
                              <span>{item.label}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    })()}
                  </div>

                  <div className="sml-view-card sml-view-acceptance-panel">
                    <h5 className="sml-view-acceptance-heading">Additional Notes</h5>
                    <p className="sml-view-acceptance-note">
                      {selectedManifest.acceptanceDetails.additionalNotes || 'No additional remarks provided.'}
                    </p>
                  </div>
                </div>

                <div className="sml-view-card sml-view-acceptance-panel sml-view-acceptance-doc">
                  <h5 className="sml-view-acceptance-heading">Proof of Delivery (POD)</h5>
                  <div className="sml-doc-row">
                    <div className="sml-doc-info">
                      <p className="sml-doc-label">Signed Delivery Receipt</p>
                      {selectedManifest.acceptanceDetails.podFile ? (
                        <>
                          <p className="sml-doc-file">{selectedManifest.acceptanceDetails.podFile.name}</p>
                          {selectedManifest.acceptanceDetails.podFile.uploadedAt && (
                            <p className="sml-doc-meta">
                              Uploaded on{' '}
                              {formatDate(selectedManifest.acceptanceDetails.podFile.uploadedAt)}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="sml-doc-file sml-doc-file-muted">No POD uploaded</p>
                      )}
                    </div>
                    {selectedManifest.acceptanceDetails.podFile ? (
                      <button
                        type="button"
                        className="sml-doc-view-btn"
                        onClick={() =>
                          handleDownloadDocument('pod', selectedManifest.acceptanceDetails.podFile)
                        }
                      >
                        View
                      </button>
                    ) : (
                      <span className="sml-chip sml-chip-muted">Pending</span>
                    )}
                  </div>
                </div>
              </div>
            )}

        </>
      )}

      {isAcceptModalOpen && acceptManifest && (
        <div className="sml-accept-modal-overlay" onClick={handleCloseAcceptModal}>
          <div
            className="sml-accept-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sml-accept-title"
            aria-describedby="sml-accept-description"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="sml-view-modal-close" onClick={handleCloseAcceptModal} aria-label="Close accept shipment">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <div className="sml-accept-header">
              <div>
                <h2 id="sml-accept-title">Accept Shipment - {acceptManifest.manifestNumber}</h2>
                <p id="sml-accept-description">Review shipment details and confirm acceptance.</p>
              </div>
            </div>

            <div className="sml-accept-stats">
              <div>
                <p className="sml-accept-stat-label">Expected Packages</p>
                <p className="sml-accept-stat-value">{acceptManifest.packages}</p>
              </div>
              <div>
                <p className="sml-accept-stat-label">Gross Weight</p>
                <p className="sml-accept-stat-value">{acceptManifest.weight}</p>
              </div>
              <div>
                <p className="sml-accept-stat-label">Invoices</p>
                <p className="sml-accept-stat-value">{acceptManifest.invoiceCount}</p>
              </div>
            </div>

            <form className="sml-accept-form" onSubmit={handleAcceptSubmit}>
              <div className="sml-accept-card">
                <h4>Receiving Details</h4>
                <div className="sml-accept-grid sml-accept-grid-two">
                  <label>
                    <span>Date Received *</span>
                    <input
                      type="date"
                      className="input-control"
                      value={acceptForm.dateReceived}
                      onChange={(e) => handleAcceptFormChange('dateReceived', e.target.value)}
                      required
                    />
                  </label>
                  <label>
                    <span>Received By *</span>
                    <input
                      type="text"
                      className="input-control"
                      placeholder="Name of person receiving"
                      value={acceptForm.receivedBy}
                      onChange={(e) => handleAcceptFormChange('receivedBy', e.target.value)}
                      required
                    />
                  </label>
                  <label>
                    <span>Number of Packages Received *</span>
                    <input
                      type="number"
                      className="input-control"
                      min="0"
                      value={acceptForm.packagesReceived}
                      onChange={(e) => handleAcceptFormChange('packagesReceived', e.target.value)}
                      required
                    />
                  </label>
                  <label>
                    <span>Condition *</span>
                    <select
                      className="input-control input-control-select"
                      value={acceptForm.condition}
                      onChange={(e) => handleAcceptFormChange('condition', e.target.value)}
                    >
                      <option>Good Condition</option>
                      <option>Minor Damage</option>
                      <option>Major Damage</option>
                    </select>
                  </label>
                </div>
              </div>

              <div className="sml-accept-row">
                <div className="sml-accept-card">
                  <h4>Inspection Checklist</h4>
                  <div className="sml-accept-checklist">
                    {[
                      { key: 'packagingIntact', label: 'Packaging is intact and undamaged' },
                      { key: 'correctQuantity', label: 'Correct quantity received as per manifest' },
                      { key: 'noVisibleDamage', label: 'No visible damage to goods' },
                      { key: 'sealsIntact', label: 'Container seals are intact and match manifest' },
                      { key: 'documentsComplete', label: 'All shipping documents are complete and correct' }
                    ].map(item => (
                      <label key={item.key}>
                        <input 
                          type="checkbox" 
                          checked={acceptForm.inspectionChecklist[item.key] || false}
                          onChange={(e) => {
                            setAcceptForm(prev => ({
                              ...prev,
                              inspectionChecklist: {
                                ...prev.inspectionChecklist,
                                [item.key]: e.target.checked
                              }
                            }));
                          }}
                          disabled={acceptingShipment}
                        />
                        <span>{item.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="sml-accept-card">
                  <h4>Additional Notes</h4>
                  <textarea
                    className="input-control sml-accept-notes"
                    rows="5"
                    placeholder="Include any special handling instructions, delivery notes, loading/unloading requirements, customs clearance notes, or other relevant information..."
                    value={acceptForm.notes}
                    onChange={(e) => handleAcceptFormChange('notes', e.target.value)}
                  />
                </div>
              </div>

              <div className="sml-accept-card">
                <h4 className="sml-accept-upload-title">
                  Proof of Delivery (POD)
                </h4>
                <label className="sml-accept-upload">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleAcceptFileChange}
                    disabled={acceptingShipment}
                  />
                  <div>
                    <p>Upload signed delivery receipt</p>
                    <span>{acceptForm.uploadName || 'PDF or image format'}</span>
                  </div>
                </label>
              </div>

              <div className="sml-accept-actions">
                <button 
                  type="button" 
                  onClick={handleCloseAcceptModal}
                  disabled={acceptingShipment}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={acceptingShipment}
                >
                  {acceptingShipment ? 'Accepting...' : 'Accept Shipment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
